/**
 * Device progress export / import / reset.
 * No accounts — a JSON blob is the backup.
 */

/** Keys that count as "progress" (not audio prefs). */
export const PROGRESS_KEYS = [
  "sots-collection-v1",
  "sots-campaign-proclaim-v1",
  "sots-decks-v3",
  "sots-deck-v2:proclaim",
  "sots-player-deck-v1",
  "sots-endless-best-v1",
  "sots-endless-packs-v1",
  "sots-coach-v1-dismissed",
  "sots-coach-v2",
] as const;

/** Optional prefs included in backup when requested. */
export const PREF_KEYS = [
  "sots-audio-v1",
  "sots-audio-v4",
  "sots-combat-feedback-v1",
  "sots-whats-new-v1",
] as const;

export const BACKUP_FORMAT = "sots-progress" as const;
export const BACKUP_VERSION = 1;

export type ProgressBackup = {
  format: typeof BACKUP_FORMAT;
  version: number;
  exportedAt: string;
  /** Raw localStorage string values by key. */
  data: Record<string, string>;
};

export type BackupSummary = {
  keys: string[];
  hasCollection: boolean;
  hasCampaign: boolean;
  hasDecks: boolean;
  hasEndless: boolean;
  exportedAt?: string;
};

function readKey(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  localStorage.setItem(key, value);
}

function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Snapshot current progress (+ optional audio) into a backup object. */
export function exportProgress(opts?: {
  includeAudio?: boolean;
}): ProgressBackup {
  const keys: string[] = [...PROGRESS_KEYS];
  if (opts?.includeAudio !== false) {
    keys.push(...PREF_KEYS);
  }
  const data: Record<string, string> = {};
  for (const key of keys) {
    const v = readKey(key);
    if (v != null && v !== "") data[key] = v;
  }
  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    data,
  };
}

export function backupToJson(backup: ProgressBackup, pretty = true): string {
  return pretty ? JSON.stringify(backup, null, 2) : JSON.stringify(backup);
}

export function parseBackup(raw: string): ProgressBackup {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Not valid JSON. Choose a .json backup file.");
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("Backup file is empty or invalid.");
  }
  const o = parsed as Partial<ProgressBackup>;
  if (o.format !== BACKUP_FORMAT) {
    throw new Error(
      `Unrecognized backup format (expected “${BACKUP_FORMAT}”).`,
    );
  }
  if (typeof o.version !== "number" || o.version < 1) {
    throw new Error("Backup version is missing or unsupported.");
  }
  if (!o.data || typeof o.data !== "object" || Array.isArray(o.data)) {
    throw new Error("Backup has no progress data.");
  }
  const data: Record<string, string> = {};
  for (const [k, v] of Object.entries(o.data)) {
    if (typeof v === "string" && k.length) data[k] = v;
  }
  if (!Object.keys(data).length) {
    throw new Error("Backup data is empty.");
  }
  return {
    format: BACKUP_FORMAT,
    version: o.version,
    exportedAt:
      typeof o.exportedAt === "string" ? o.exportedAt : new Date().toISOString(),
    data,
  };
}

export function summarizeBackup(backup: ProgressBackup): BackupSummary {
  const keys = Object.keys(backup.data);
  return {
    keys,
    hasCollection: keys.includes("sots-collection-v1"),
    hasCampaign: keys.includes("sots-campaign-proclaim-v1"),
    hasDecks: keys.some((k) => k.startsWith("sots-deck")),
    hasEndless: keys.some((k) => k.startsWith("sots-endless")),
    exportedAt: backup.exportedAt,
  };
}

/**
 * Write backup into localStorage.
 * Clears all known progress keys first so removed keys don't linger,
 * then restores from the file. Audio is only overwritten if present in backup.
 */
export function importProgress(backup: ProgressBackup): BackupSummary {
  const allowed = new Set<string>([...PROGRESS_KEYS, ...PREF_KEYS]);
  for (const key of PROGRESS_KEYS) {
    removeKey(key);
  }
  for (const [key, value] of Object.entries(backup.data)) {
    if (!allowed.has(key)) continue;
    writeKey(key, value);
  }
  return summarizeBackup(backup);
}

/**
 * Wipe progress on this device. Audio settings kept unless includeAudio.
 */
export function resetProgress(opts?: { includeAudio?: boolean }): void {
  for (const key of PROGRESS_KEYS) {
    removeKey(key);
  }
  if (opts?.includeAudio) {
    for (const key of PREF_KEYS) {
      removeKey(key);
    }
  }
}

/** Human-readable snapshot of what is currently stored. */
export function currentProgressSummary(): {
  present: string[];
  missing: string[];
} {
  const present: string[] = [];
  const missing: string[] = [];
  for (const key of PROGRESS_KEYS) {
    const v = readKey(key);
    if (v != null && v !== "") present.push(key);
    else missing.push(key);
  }
  return { present, missing };
}

/**
 * Player-facing categories present on this device (no raw storage keys).
 * Used by Settings — keep labels short and non-technical.
 */
export type PlayerSaveArea = {
  id: "campaign" | "collection" | "decks" | "endless" | "audio";
  label: string;
};

export function playerSaveAreasOnDevice(): PlayerSaveArea[] {
  const has = (key: string) => {
    const v = readKey(key);
    return v != null && v !== "";
  };
  const areas: PlayerSaveArea[] = [];
  if (has("sots-campaign-proclaim-v1")) {
    areas.push({ id: "campaign", label: "Campaign" });
  }
  if (has("sots-collection-v1")) {
    areas.push({ id: "collection", label: "Collection & gallery" });
  }
  if (
    has("sots-decks-v3") ||
    has("sots-deck-v2:proclaim") ||
    has("sots-player-deck-v1")
  ) {
    areas.push({ id: "decks", label: "Decks" });
  }
  if (has("sots-endless-best-v1") || has("sots-endless-packs-v1")) {
    areas.push({ id: "endless", label: "Endless" });
  }
  if (has("sots-audio-v1")) {
    areas.push({ id: "audio", label: "Audio settings" });
  }
  return areas;
}

export function downloadBackupFile(backup: ProgressBackup): void {
  const json = backupToJson(backup);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const day = backup.exportedAt.slice(0, 10);
  a.href = url;
  a.download = `sword-of-the-spirit-progress-${day}.json`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function labelForKey(key: string): string {
  switch (key) {
    case "sots-collection-v1":
      return "Card collection & gallery";
    case "sots-campaign-proclaim-v1":
      return "Campaign map";
    case "sots-decks-v3":
      return "Saved decks";
    case "sots-deck-v2:proclaim":
    case "sots-player-deck-v1":
      return "Legacy deck data";
    case "sots-endless-best-v1":
      return "Endless high scores";
    case "sots-endless-packs-v1":
      return "Endless pack progress";
    case "sots-coach-v1-dismissed":
      return "Coach tutorial flag (legacy)";
    case "sots-coach-v2":
      return "Coach tutorial progress";
    case "sots-audio-v1":
      return "Audio settings";
    case "sots-whats-new-v1":
      return "What’s new (last seen)";
    default:
      return key;
  }
}
